<script>
import {useCartStore} from "@js/store/CartStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import {IMaskDirective} from "vue-imask";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import {computed} from "vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import IconExclamationFilled from "@js/components/Icons/IconExclamationFilled.vue";

export default {
    name: "CartForm",
    components: {
        IconExclamationFilled,
        SpinnerIndicator,
        PrivacyPolicyAgreementLink,
        CallbackErrorMessage,
        NumberFormatComponent,
    },
    directives: {imask: IMaskDirective},

    setup() {
        const cartStore = useCartStore();

        const productsSum = computed(() => cartStore.subTotal);
        const total = computed(() => productsSum.value);

        const phoneMask = {mask: "+7 (000) 000-00-00"};
        const innMask = {mask: "000000000000"};

        const updatePhone = (val) => {
            cartStore.form.phone = val;
        };

        const updateInn = (val) => {
            cartStore.form.inn = val;
        };

        const sending = computed(() => !!cartStore.loadings?.sending);

        return {
            cartStore,
            productsSum,
            total,
            phoneMask,
            updatePhone,
            innMask,
            updateInn,
            sending,
        };
    },
};
</script>

<template>
    <div class="flex flex-col bg-grayBg rounded-2xl p-4 gap-5">
        <div>
            <h2 class="text-lg font-medium mb-3">Стоимость заказа</h2>

            <div
                role="alert"
                class="w-full font-medium flex items-start px-4 py-3 text-sm text-blue-800 border border-blue-300 rounded-lg bg-blue-50"
            >
                <IconExclamationFilled class="shrink-0 inline w-5 h-5 me-2 mt-0.5"/>
                <span>
                    Стоимость доставки не входит в сумму заказа. Менеджер рассчитает её после отправки заявки.
                </span>
            </div>

            <div class="flex items-end justify-between w-full mt-4">
                <div class="font-medium">Итого по товарам</div>
                <div class="font-bold">
                    <NumberFormatComponent :number="total" append_text=" ₽"/>
                </div>
            </div>
        </div>

        <div class="flex flex-col gap-3">
            <h2 class="text-lg font-medium">Оформление заказа</h2>

            <div>
                <input
                    type="text"
                    placeholder="Ф.И.О.*"
                    v-model="cartStore.form.full_name"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['full_name'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'full_name'"/>
            </div>

            <div>
                <input
                    type="tel"
                    :value="cartStore.form.phone"
                    v-imask="phoneMask"
                    @keyup="updatePhone($event.target.value)"
                    placeholder="+7 (___) ___-__-__*"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['phone'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'phone'"/>
            </div>

            <div>
                <input
                    type="email"
                    placeholder="Почта"
                    v-model="cartStore.form.email"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['email'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'email'"/>
            </div>

            <div v-if="cartStore.form.payment_type === 'invoice'">
                <input
                    type="text"
                    :value="cartStore.form.inn"
                    v-imask="innMask"
                    @keyup="updateInn($event.target.value)"
                    placeholder="ИНН*"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['inn'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'inn'"/>
            </div>

            <div class="mt-2">
                <textarea
                    rows="4"
                    v-model="cartStore.form.comment"
                    class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border-2 border-gray-300 focus:ring-0 focus:border-baseColor"
                    placeholder="Комментарий к заказу..."
                ></textarea>
            </div>

            <div class="mt-5">
                <PrivacyPolicyAgreementLink/>
            </div>

            <div>
                <button
                    type="button"
                    @click.prevent="cartStore.sendForm"
                    :disabled="sending"
                    class="flex items-center justify-center w-full gap-2 leading-tight uppercase text-nowrap bg-baseColor md:hover:bg-baseColor/80 text-black focus:outline-none focus:ring-0 font-bold rounded-xl text-sm px-5 py-4 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    aria-live="polite"
                >
                    <SpinnerIndicator v-if="sending" class="w-5 h-5"/>
                    {{ sending ? 'Отправляем заявку…' : 'Отправить заявку' }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
